import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect, ChangeEvent } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import {
  dateMask,
  displayDateTime,
  filesAllowed,
  handleFocus,
} from "../../../utils/masks";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getValidator } from "../../../utils/validatorsYup";

import { authenticationService } from "../../../services/auth.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";

import "../../shared/modal.css";
import "../../shared/button.css";
import IEventDetail from "../../../models/IEventDetail";
import moment from "../../../utils/momentConfig";
import CurrencyInput from "../../../components/CurrencyInput";
import CouponList from "../../coupon/coupon-list/CouponList";
import { CouponScope } from "../../../models/enums/CouponScope";

type EventParams = {
  id: string;
}

export default function EventAddEdit() {
  const params = useParams<EventParams>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<IEventDetail>();
  const [bannerImage, setBannerImage] = useState<File | string>();
  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [canChange, setCanChange] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required("Nome obrigatório")
      .max(100, (val) => `Máximo ${val.max} caracteres`)
      .min(3, (val) => `Mínimo ${val.min} caracteres`),
    note: yup.string()
      .max(500, (val) => `Máximo ${val.max} caracteres`),
    integrationProvider: yup.string(),
    endDate: getValidator({ isDate: true }).required("Final do evento obrigatório"),
    individualAmount: yup.number()
      .when("requirePayment", {
        is: true,
        then: getValidator({isNumeric: true, numberGreaterThanZero: true}).required("Valor individual obrigatório"),
      }),
    allowCouple: yup.boolean(),
    requirePayment: yup.boolean(),
    coupleAmount: yup.number()
      .when("allowCouple", {
        is: true,
        then: getValidator({isNumeric: true, numberGreaterThanZero: true}).required("Valor individual obrigatório"),
      }),
    individualAcceptCoupon: yup.boolean(),
    coupleAcceptCoupon: yup.boolean()
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState,
    setValue,
    trigger,
    watch
  } = useForm({
    resolver: yupResolver(schema),
  });

  const allowCouple = watch("allowCouple", event?.allowCouple ?? false);
  const requirePayment = watch("requirePayment", event?.requirePayment ?? false);
  const hasIntegration = watch("integrationProvider", (event?.integrationProvider && event?.integrationProvider?.trim() != "") ?? false);

  useEffect(() => {
    const init = async () => {
      const id = +(params.id ?? 0);
      const adding = id ? id <= 0 : true;

      if (!adding) {
        handleEdit(id);
      }

      setDisabled((authenticationService.isAuxSec() && !adding) || authenticationService.isReadOnly());
      setCanChange(authenticationService.canChange());
    };

    init();
  }, [params.id]);

  const handleEdit = async (id: number) => {
    try {
      const { data } = await api.get(`events/${id}`);
      setEvent(data.item);
      setBannerImage(data.item.bannerUrl);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    }
  };

  const onSubmit = async (data: IEventDetail) => {
    try {
      const formData = new FormData();

      data.id = event?.id ?? 0;
      data.requirePayment = !!data.requirePayment;
      if (data.requirePayment) {
        data.allowCouple = !!data.allowCouple;
  
        if (!data.allowCouple) {
          data.coupleAmount = 0;
        }
      }
      
      if (bannerImage && event?.bannerUrl != bannerImage) {
        formData.append("image", bannerImage);
        data.bannerUrl = "updated";
      }

      formData.append("data", JSON.stringify(data));

      if (!event?.id || event?.id == 0) {
        const result = await api.post("events", formData);
        toast.success("Evento cadastrado com sucesso!", toastOptions);
        navigate(`/eventos/${result.data.item.id}/dashboard`);
      } else {
        const { data: result } = await api.put("events", formData);
        toast.success("Evento atualizado com sucesso!", toastOptions);
        setEvent(result.item);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleSelectImage = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const img = event.target.files[0];

    if (!filesAllowed(img)) {
      toast.warn("Permitido apenas imagens", toastOptions);
      return;
    }

    setBannerImage(img);
  };

  return (params.id && !event && +(params.id) > 0) ? (
    <div className="uk-flex uk-flex-center">
      <div uk-spinner="true; ratio:2">Carregando...</div>
    </div>
  ) : (
    <div id="page-add-edit-event" className="uk-section uk-section-default uk-padding-small">
      {event?.id && authenticationService.canChange() && (
        <ul uk-tab="true">
          <li className={tabIndex == 0 ? "uk-active" : ""} onClick={() => setTabIndex(0)}>
            <a
              href="#"
              onClick={(ev) => {
                ev.preventDefault();
              }}
            >
              Dados
            </a>
          </li>
          {event.requirePayment && 
            <li className={tabIndex == 1 ? "uk-active" : ""} onClick={() => setTabIndex(1)}>
              <a
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                }}
              >
                Cupons
              </a>
            </li>
          }
        </ul>
      )}
      <form
        hidden={tabIndex != 0}
        onSubmit={handleSubmit<IEventDetail>(onSubmit)}
        onReset={reset}
      >
        <div className="uk-modal-body">
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1@s">
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-3-4@s">
                  <label className="uk-form-label" htmlFor="name">
                    Nome do Evento
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Nome do Evento"
                    className="uk-input uk-form-small uk-text-uppercase"
                    defaultValue={event?.name}
                    autoComplete={"off"}
                    onFocus={handleFocus}
                    ref={register}
                    disabled={isDisabled}
                  />
                  <small className="uk-text-danger">{errors.name?.message}</small>
                </div>
                <div className="uk-width-1-4@s">
                  <label className="uk-form-label" htmlFor="endDate">
                    Final do Evento
                  </label>
                  <input
                    type="text"
                    id="endDate"
                    name="endDate"
                    placeholder="99/99/9999"
                    className="uk-input uk-form-small uk-text-uppercase"
                    defaultValue={event?.endDate ? moment(event.endDate).format('DD/MM/YYYY') : ''}
                    autoComplete={"off"}
                    onFocus={handleFocus}
                    ref={register}
                    disabled={isDisabled}
                    onChange={(e) => {e.target.value = dateMask(e.target.value);}}
                  />
                  <small className="uk-text-danger">{errors.endDate?.message}</small>
                </div>
                <div className="uk-width-1@s">
                  <label className="uk-form-label" htmlFor="requirePayment">
                    Exige Pagamento?
                  </label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-checkbox"
                      type="checkbox"
                      id="requirePayment"
                      name="requirePayment"
                      defaultChecked={event?.requirePayment ?? false}
                      ref={register}
                      onChange={(e) => setEvent({...event,
                        individualAmount: 0,
                        coupleAmount: 0,
                        allowCouple: false,
                        integrationProvider: '',
                        requirePayment: e.target.checked
                      } as IEventDetail)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
                {requirePayment && <>
                
                  <div className="uk-width-1-4@s">
                    <CurrencyInput
                      name="individualAmount"
                      label="Valor Individual"
                      placeholder="Valor Individual"
                      defaultValue={event?.individualAmount}
                      register={register}
                      setValue={setValue}
                      trigger={trigger}
                      disabled={isDisabled}
                      onChange={(value) => setEvent({...event, individualAmount: value} as IEventDetail)}
                      error={errors.individualAmount?.message}
                    />
                  </div>  
                  <div className="uk-width-1-6@s" uk-tooltip="title: Permitir uso de cupom para individual; pos: top">
                    <label className="uk-form-label" htmlFor="individualAcceptCoupon">
                      Permitir Cupom?
                    </label>
                    <div className="uk-form-controls">
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="individualAcceptCoupon"
                        name="individualAcceptCoupon"
                        defaultChecked={event?.individualAcceptCoupon ?? false}
                        ref={register}
                        disabled={isDisabled}
                        onChange={(e) => setEvent({...event, individualAcceptCoupon: e.target.checked} as IEventDetail)}
                      />
                    </div>
                  </div>              
                  <div className="uk-width-1-6@s">
                    <label className="uk-form-label" htmlFor="allowCouple">
                      Permitir Casal?
                    </label>
                    <div className="uk-form-controls">
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="allowCouple"
                        name="allowCouple"
                        defaultChecked={event?.allowCouple ?? false}
                        ref={register}
                        disabled={isDisabled}
                        onChange={(e) => setEvent({...event, allowCouple: e.target.checked} as IEventDetail)}
                      />
                    </div>
                  </div>
                  {allowCouple && <>
                    <div className="uk-width-1-4@s">
                      <CurrencyInput
                        name="coupleAmount"
                        label="Valor Casal"
                        placeholder="Valor Casal"
                        defaultValue={event?.coupleAmount}
                        register={register}
                        setValue={setValue}
                        trigger={trigger}
                        disabled={isDisabled}
                        onChange={(value) => setEvent({...event, coupleAmount: value} as IEventDetail)}
                        error={errors.coupleAmount?.message}
                      />
                    </div>
                    <div className="uk-width-1-6@s" uk-tooltip="title: Permitir uso de cupom para casal; pos: top">
                      <label className="uk-form-label" htmlFor="coupleAcceptCoupon">
                        Cupom Casal?
                      </label>
                      <div className="uk-form-controls">
                        <input
                          className="uk-checkbox"
                          type="checkbox"
                          id="coupleAcceptCoupon"
                          name="coupleAcceptCoupon"
                          defaultChecked={event?.coupleAcceptCoupon ?? false}
                          ref={register}
                          disabled={isDisabled}
                          onChange={(e) => setEvent({...event, 
                            coupleAcceptCoupon: e.target.checked,
                            coupleAmount: 0
                          } as IEventDetail)}
                        />
                      </div>
                    </div>
                  </>}
                  <div className="uk-width-1-3@s">
                    <label className="uk-form-label" htmlFor="integrationProvider">
                      Integração Pagamento
                      {hasIntegration && <FontAwesomeIcon icon={faCircleExclamation} className="uk-margin-xsmall-x uk-text-primary" />}
                      {hasIntegration && <div className="uk-margin-xsmall-y uk-flex uk-flex-middle">
                        <span className="uk-text-small uk-text-primary">Evento com integração, onde o vencimento será <b>7 dias</b> antes da data <b>Final do Evento</b></span>
                      </div>}
                    </label>
                    <div
                      uk-form-custom="target: > * > span:first-child"
                      className="uk-width-1"
                    >
                      <select
                        className="uk-form-small"
                        id="integrationProvider"
                        name="integrationProvider"
                        defaultValue={event?.integrationProvider ?? ""}
                        ref={register}
                        disabled={isDisabled}
                        onChange={(e) => setEvent({...event, integrationProvider: e.target.value} as IEventDetail)}
                      >
                        <option value="" key="integrationProvider0">
                          Selecione...
                        </option>
                        <option value="ASAAS" key="integrationProvider1">
                          ASAAS
                        </option>
                      </select>
                      <button
                        className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                        style={{ textTransform: "none" }}
                        type="button"
                        tabIndex={-1}
                        disabled={isDisabled}
                      >
                        <span></span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                    <small className="uk-text-danger">
                      {errors.integrationProvider?.message}
                    </small>
                  </div>
                </>}
                <div className="uk-width-1@s">
                  <label className="uk-form-label" htmlFor="note">
                    Observações
                  </label>
                  <textarea
                    id="note" 
                    name="note"
                    className="uk-textarea uk-form-small uk-text-uppercase"
                    rows={6} 
                    style={{resize: 'none'}}
                    placeholder="Observações sobre o evento"
                    defaultValue={event?.note}
                    autoComplete={"off"}
                    ref={register}
                    disabled={isDisabled}
                  />
                  <small className="uk-text-danger">{errors.note?.message}</small>
                </div>
              </div>
            </div>
            <div className="uk-width-1-1@s uk-card uk-card-default uk-card-body uk-flex uk-flex-column uk-flex-middle uk-flex-center">
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1@s uk-flex uk-flex-column uk-flex-middle">
                  {bannerImage ? (
                    <img
                      style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                      src={
                        typeof bannerImage == "string"
                          ? `data:image/jpeg;base64,${bannerImage}`
                          : URL.createObjectURL(bannerImage)
                      }
                      alt="Banner do Evento"
                    />
                  ) : (
                    <span uk-icon="icon: image; ratio: 4"></span>
                  )}
                  {!isDisabled && (
                    <div className="js-upload" uk-form-custom="true">
                      <input
                        type="file"
                        id="file-input-banner"
                        accept="image/jpg, image/png, image/jpeg, image/webp"
                        multiple={false}
                        onChange={handleSelectImage}
                        disabled={isDisabled}
                      />
                      <button
                        className="uk-button uk-button-danger uk-button-small uk-margin-small-top"
                        type="button"
                        tabIndex={-1}
                        disabled={isDisabled}
                      >
                        Banner
                        <FontAwesomeIcon
                          className="uk-margin-small-left"
                          icon={faCamera}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>            
          </div>
          {event?.id && canChange && (
            <div className="uk-grid-small uk-margin-top" uk-grid="true">
              <div className="uk-width-1-2@s">
                <span className="uk-text-italic uk-text-small">
                  Adicionado por:
                </span>
                <span className="uk-text-muted uk-text-small uk-margin-small-left">
                  {`${event?.createdBy} - ${displayDateTime(event?.createdDate)}`}
                </span>
              </div>
              {(event).modifiedBy && (
                <div className="uk-width-1-2@s">
                  <span className="uk-text-italic uk-text-small">
                    Alterado por:
                  </span>
                  <span className="uk-text-muted uk-text-small uk-margin-small-left">
                    {`${event?.modifiedBy} - ${displayDateTime(event?.modifiedDate)}`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="uk-modal-footer uk-margin-bottom">
          <div className="uk-grid-small uk-flex uk-flex-center" uk-grid="true">
            <div className="uk-text-center">
              <Link
                to="/eventos"
                className="uk-button uk-button-default uk-button-small uk-margin-left uk-width-1-1"
              >
                Voltar
              </Link>
            </div>
            <div className="uk-text-center">
              <button
                id="btn-save"
                disabled={formState.isSubmitting || isDisabled || !!event?.closeDate}
                className="uk-button uk-button-primary uk-button-small uk-margin-left uk-width-1-1"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </div>
      </form>
      {event?.id && event.requirePayment && tabIndex == 1 && <CouponList scope={CouponScope.EVENT} idScope={event.id ?? 0} />}
    </div>
  );
}
